import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import MicIcon from '@mui/icons-material/Mic';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { chatMarkdownComponents } from './utils/chatTable';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, getAuthHeaders, getUser } from './auth';
import { formatDate } from './utils/formatDate';
import useSpeechToText from './hooks/useSpeechToText';
import useScrollToBottom from './hooks/useScrollToBottom';
import AreDisclaimer from './AreDisclaimer';
import DossierView from './DossierView';
import {
  chatBubbleSx,
  chatRowSx,
  BotAvatar,
  ModalHeader,
  InfoTooltip,
  modalHeaderBtnSx,
  FIRST_LINE_SLOT_PX,
  ScrollToBottomButton,
} from './chatMessageStyles';
import './HeyBroInsurance.css';
import { newConversationId } from './activity/track';

/**
 * HeyBro Insurance — chat agent + shared company-dossier library.
 *
 * Tab "Assistent": talks to POST /api/dossier/chat (fetch-SSE, stateless — the
 * full history rides along with every request, persisted per user in
 * localStorage; same pattern as EfdAgentChat). The agent's tools emit
 * `dossier_started` / `dossier_open` / `dossier_deleted` events this component
 * acts on: a started run switches to the live research view.
 *
 * Tab "Dossiers": the shared library (every user sees every dossier), the live
 * "market research" visualization while a run is in progress (ARE pattern:
 * fire-and-forget POST /run + 2s polling + watchdog re-kick), and the
 * DossierView magazine for finished dossiers.
 */

const STT_FIELD = 'heybro-insurance';
const STORAGE_KEY_PREFIX = 'heybro_insurance_session';
const IN_PROGRESS = ['planning', 'collecting', 'analyzing', 'composing', 'refreshing'];

/** GET one dossier in the viewer's language (see the language handling note
 * on the re-fetch effect below). */
const reportUrl = (id, language) =>
  `${API_BASE_URL}/api/dossier/reports/${id}?language=${encodeURIComponent(language || 'de')}`;

const storageKey = () => {
  const user = getUser();
  const id = user && (user.id ?? user.username);
  return id != null ? `${STORAGE_KEY_PREFIX}:${id}` : `${STORAGE_KEY_PREFIX}:anonymous`;
};

const loadSession = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey()));
    return Array.isArray(saved) ? saved.filter(m => m && m.role && m.content) : [];
  } catch (e) {
    return [];
  }
};

/** Read an SSE response body and hand each complete event to `onEvent`. */
async function readSseStream(res, onEvent) {
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const handle = (rawEvent) => {
    let eventType = '';
    let eventData = '';
    for (const line of rawEvent.split('\n')) {
      if (line.startsWith('event: ')) eventType = line.slice(7).trim();
      else if (line.startsWith('data: ')) eventData = line.slice(6);
    }
    if (!eventType || !eventData) return;
    try {
      onEvent(eventType, JSON.parse(eventData));
    } catch (e) {
      console.warn('Failed to parse SSE data:', eventData, e);
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      buffer += decoder.decode();
      buffer.split('\n\n').forEach(ev => ev.trim() && handle(ev));
      return;
    }
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop();          // keep the trailing partial event
    events.forEach(ev => ev.trim() && handle(ev));
  }
}

// No `language` prop: the UI language comes from i18n. The agent answers in
// the language of the user's latest message (decided server-side); the UI
// language rides along only as the fallback for messages without one. Dossier
// content is always German (product decision — the boss's prompts prescribe it).
function HeyBroInsurance({ onClose }) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState(0);                       // 0 assistant | 1 dossiers
  const [messages, setMessages] = useState(loadSession);   // {role, content}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [dossiers, setDossiers] = useState([]);
  const [dossiersLoading, setDossiersLoading] = useState(false);
  // null = list; {mode:'live', id} = live research; {mode:'dossier', report} = magazine
  const [view, setView] = useState(null);
  const [runSteps, setRunSteps] = useState([]);
  const [runPhase, setRunPhase] = useState('collecting');
  // True while the backend is producing this dossier's translation in the
  // background; the canonical (German) text stays readable meanwhile.
  const [translating, setTranslating] = useState(false);
  const [runCompany, setRunCompany] = useState('');
  const [runError, setRunError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, dossier: null });
  const [deleting, setDeleting] = useState(false);
  const chatScrollRef = useRef(null);
  const abortRef = useRef(null);
  const pollRef = useRef(null);
  const inputRef = useRef(null);
  // The run poller is a long-lived interval whose closure is created once —
  // it reads the CURRENT language through this ref instead of capturing it,
  // so switching language mid-run does not require restarting the poll.
  const i18nRef = useRef(i18n);
  i18nRef.current = i18n;

  const focusInput = useCallback(() => {
    setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0);
  }, []);

  const {
    showScrollDown, bottomSpacerRef, messagesEndRef, handleScroll,
    scrollToBottom, markUserSent, syncOnContentChange,
  } = useScrollToBottom(chatScrollRef);

  const { recordingField, transcribingField, toggle: toggleRecording } = useSpeechToText({
    onResult: (_field, text) => setInput((prev) => (prev ? `${prev} ${text}` : text)),
  });
  const isRecording = recordingField === STT_FIELD;
  const isTranscribing = transcribingField === STT_FIELD;

  useLayoutEffect(() => { syncOnContentChange(); }, [messages, busy, syncOnContentChange]);
  useLayoutEffect(() => {
    messagesEndRef.current?.scrollIntoView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist after every change, including mid-stream.
  useEffect(() => {
    try {
      if (messages.length) localStorage.setItem(storageKey(), JSON.stringify(messages));
      else localStorage.removeItem(storageKey());
    } catch (e) { /* private mode / quota — the chat still works in memory */ }
  }, [messages]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => () => { abortRef.current?.abort(); stopPolling(); }, [stopPolling]);

  // ── Dossier library ──
  const fetchDossiers = useCallback(async () => {
    setDossiersLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/dossier/reports`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDossiers(Array.isArray(data.reports) ? data.reports : []);
      }
    } catch (e) { /* ignore */ } finally { setDossiersLoading(false); }
  }, []);

  useEffect(() => { fetchDossiers(); }, [fetchDossiers]);

  /**
   * Live research view: fire-and-forget kick + 2s polling + watchdog (the ARE
   * pattern — the DB is the source of truth, no long-lived connection).
   */
  const watchRun = useCallback((id, { kick = false } = {}) => {
    stopPolling();
    setTab(1);
    setView({ mode: 'live', id });
    setRunSteps([]);
    setRunPhase('collecting');
    setRunError('');

    if (kick) {
      fetch(`${API_BASE_URL}/api/dossier/reports/${id}/run`, { method: 'POST', headers: getAuthHeaders() }).catch(() => {});
    }

    let stuckTicks = 0;
    let done = false;
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(reportUrl(id, i18nRef.current.language), { headers: getAuthHeaders() });
        if (!res.ok) return;
        const rep = await res.json();
        setRunCompany(rep.company_name || '');
        const prog = rep.progress || {};
        setRunPhase(prog.phase || 'collecting');
        setRunSteps(prog.steps || []);
        // Watchdog: a run stuck on 'planning' (or a 'refreshing' row with no
        // progress yet, e.g. after a backend restart) gets re-kicked — /run is
        // idempotent server-side.
        const stuck = rep.status === 'planning'
          || (rep.status === 'refreshing' && !(prog.steps || []).length);
        if (stuck) {
          stuckTicks += 1;
          if (stuckTicks % 3 === 0) {
            try { await fetch(`${API_BASE_URL}/api/dossier/reports/${id}/run`, { method: 'POST', headers: getAuthHeaders() }); } catch (e) { /* ignore */ }
          }
        } else {
          stuckTicks = 0;
        }
        if (!done && ['ready', 'error'].includes(rep.status)) {
          done = true;
          stopPolling();
          fetchDossiers();
          if (rep.status === 'ready') {
            setView({ mode: 'dossier', report: rep });
          } else {
            setRunError(rep.error || t('heybro_error'));
          }
        }
      } catch (e) { /* keep polling */ }
    }, 2000);
  }, [stopPolling, fetchDossiers, t]);

  const openDossier = useCallback(async (id) => {
    try {
      const res = await fetch(reportUrl(id, i18n.language), { headers: getAuthHeaders() });
      if (!res.ok) return;
      const rep = await res.json();
      setTab(1);
      if (IN_PROGRESS.includes(rep.status)) watchRun(id);
      else setView({ mode: 'dossier', report: rep });
    } catch (e) { /* ignore */ }
  }, [watchRun, i18n.language]);

  /**
   * Language handling. Dossiers are researched and stored in German (shared
   * library); the backend serves other languages from its translation cache.
   * So whenever the UI language changes — or a dossier is opened — the open
   * dossier is re-fetched for that language, and while the backend reports
   * `translation_status: 'translating'` we keep polling: the canonical text
   * stays readable meanwhile and is swapped out when the translation lands.
   */
  useEffect(() => {
    const id = view?.mode === 'dossier' ? view.report?.id : null;
    if (!id) {
      setTranslating(false);
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const res = await fetch(reportUrl(id, i18n.language), { headers: getAuthHeaders() });
        if (cancelled || !res.ok) return;
        const rep = await res.json();
        if (cancelled) return;
        setView((cur) => (cur?.mode === 'dossier' && cur.report?.id === id
          ? { mode: 'dossier', report: rep } : cur));
        if (rep.translation_status === 'translating') {
          setTranslating(true);
          timer = setTimeout(tick, 2500);
        } else {
          setTranslating(false);
        }
      } catch (e) { /* keep the currently rendered dossier */ }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // `view.report` identity changes on every tick, so the effect keys on the
    // dossier ID and the language only — otherwise it would re-arm itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, view?.mode, view?.report?.id]);

  const refreshDossier = useCallback(async (id) => {
    try {
      await fetch(`${API_BASE_URL}/api/dossier/reports/${id}/refresh`, { method: 'POST', headers: getAuthHeaders() });
    } catch (e) { /* the watchdog will re-kick */ }
    watchRun(id);
  }, [watchRun]);

  // Word export: the backend renders the STORED dossier into a .docx —
  // downloaded via blob because the request needs the auth header (same
  // pattern as the chat-history download in EfdAgentChat).
  const exportDossier = useCallback(async (id, companyName) => {
    try {
      // Same language as the dossier on screen — the backend localises the
      // content through the translation cache and renders its own chrome to
      // match. It WAITS for a translation that is not cached yet, so this
      // request can take longer than a cached one.
      const res = await fetch(
        `${API_BASE_URL}/api/dossier/reports/${id}/export?language=${encodeURIComponent(i18n.language || 'de')}`,
        { headers: getAuthHeaders() });
      if (!res.ok) return;
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const fallback = `Company-Dossier_${(companyName || 'Company').replace(/[^\wÄÖÜäöüß-]+/g, '_')}.docx`;
      const url = window.URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : fallback;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* download blocked/offline — the button simply stops spinning */ }
  }, [i18n.language]);

  const confirmDelete = useCallback(async () => {
    const dossier = deleteDialog.dossier;
    if (!dossier) return;
    setDeleting(true);
    try {
      await fetch(`${API_BASE_URL}/api/dossier/reports/${dossier.id}`, { method: 'DELETE', headers: getAuthHeaders() });
    } catch (e) { /* ignore */ }
    setDeleting(false);
    setDeleteDialog({ open: false, dossier: null });
    setView((cur) => (cur && ((cur.mode === 'dossier' && cur.report.id === dossier.id) || (cur.mode === 'live' && cur.id === dossier.id)) ? null : cur));
    fetchDossiers();
  }, [deleteDialog, fetchDossiers]);

  // ── Chat ──
  const pendingConversation = useRef(null);
  const conversationId = () => {
    const first = messages.find(m => m.role === 'user');
    if (first) return first.cid;
    if (!pendingConversation.current) pendingConversation.current = newConversationId();
    return pendingConversation.current;
  };

  const clearChat = () => {
    pendingConversation.current = null;
    abortRef.current?.abort();
    setMessages([]);
    setInput('');
    setBusy(false);
    try { localStorage.removeItem(storageKey()); } catch (e) { /* ignore */ }
  };

  const send = async () => {
    const question = input.trim();
    if (!question || busy) return;
    setInput('');
    setBusy(true);
    markUserSent();

    const history = messages.map(({ role, content }) => ({ role, content }));
    const cid = conversationId();
    setMessages(m => [...m, { role: 'user', content: question, cid }, { role: 'assistant', content: '' }]);

    const abortController = new AbortController();
    abortRef.current = abortController;

    const appendToAnswer = (chunk) => {
      setMessages(m => {
        const next = [...m];
        const last = next[next.length - 1];
        next[next.length - 1] = { ...last, content: last.content + chunk };
        return next;
      });
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/dossier/chat`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ message: question, history, language: i18n.language, conversation_id: cid }),
        signal: abortController.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      await readSseStream(res, (eventType, data) => {
        if (eventType === 'text' && data.text) appendToAnswer(data.text);
        else if (eventType === 'dossier_started' && data.dossier_id) {
          // A research run (new or refresh) began — show it live.
          watchRun(data.dossier_id);
          fetchDossiers();
        } else if (eventType === 'dossier_open' && data.dossier_id) {
          openDossier(data.dossier_id);
        } else if (eventType === 'dossier_deleted') {
          fetchDossiers();
          setView((cur) => (cur && ((cur.mode === 'dossier' && cur.report.id === data.dossier_id) || (cur.mode === 'live' && cur.id === data.dossier_id)) ? null : cur));
        } else if (eventType === 'error') {
          appendToAnswer(`\n\n_${data.message || 'Fehler'}_`);
        } else if (eventType === 'done') {
          console.log('HeyBro Insurance done:', data.stats, data.tool_log);
        }
      });
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('HeyBro Insurance request failed:', e);
        appendToAnswer(`\n\n_${t('heybro_error')}_`);
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
      focusInput();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ── Render: live research visualization (ARE look, hb- classes) ──
  const renderLiveView = () => {
    const collectionSteps = runSteps.filter((s) => s.id === 'basis' || String(s.id).startsWith('track-'));
    const finalSteps = runSteps.filter((s) => String(s.id).startsWith('sec-') || s.id === 'compose');
    const trackSteps = runSteps.filter((s) => String(s.id).startsWith('track-'));
    const doneTracks = trackSteps.filter((s) => s.status === 'done').length;
    const heroKey = runPhase === 'analyzing' ? 'heybro_hero_analyzing'
      : runPhase === 'composing' || runPhase === 'done' ? 'heybro_hero_composing'
      : 'heybro_hero_collecting';

    // The pipeline writes German labels into `progress`; every step also
    // carries a stable `key` that maps to a translated label, so the radar
    // switches language instantly. Rows written before keys existed fall back
    // to the backend label.
    const stepLabel = (s) => (s.key
      ? t(`heybro_step_${s.key}`, { defaultValue: s.label || '' })
      : (s.label || ''));

    const stepRow = (s) => (
      <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Box sx={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {s.status === 'done' ? (
            <svg className="hb-check-svg" width="20" height="20" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="11" fill="rgba(61,220,151,0.12)" />
              <path d="M6 12.5l4 4 8-9" fill="none" stroke="#3ddc97" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <CircularProgress size={15} thickness={5} sx={{ color: 'primary.main' }} />
          )}
        </Box>
        <Typography sx={{ fontSize: 13, lineHeight: 1.3, color: s.status === 'done' ? 'text.secondary' : 'text.primary',
          fontWeight: s.status === 'done' ? 400 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
          {stepLabel(s)}
        </Typography>
      </Box>
    );

    if (runError) {
      return (
        <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
          <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main' }} />
          <Typography sx={{ fontWeight: 600, fontSize: 17 }}>{t('heybro_run_failed')}</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', maxWidth: 460 }}>{runError}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => setView(null)}
              sx={{ ...modalHeaderBtnSx, px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600 }}>
              {t('heybro_back')}
            </Button>
            {view?.id && (
              <Button size="small" startIcon={<RefreshIcon />} onClick={() => refreshDossier(view.id)}
                sx={{ ...modalHeaderBtnSx, px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600 }}>
                {t('heybro_retry')}
              </Button>
            )}
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ py: 3, px: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <div className="hb-research" aria-hidden="true">
          <div className="hb-glow" />
          <div className="hb-ring" />
          <div className="hb-ring hb-ring2" />
          <div className="hb-orbit"><span className="hb-dot" /></div>
          <div className="hb-orbit hb-o2"><span className="hb-dot" /></div>
          <div className="hb-lens"><TravelExploreIcon sx={{ fontSize: 88, color: 'primary.main' }} /></div>
        </div>
        <Typography sx={{ fontWeight: 600, fontSize: 19, mt: 2 }}>{t(heroKey)}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('heybro_hero_subtitle')}</Typography>

        {runCompany && (
          <Chip size="small" label={runCompany}
            sx={{ mt: 1.5, height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: 'rgba(25,208,239,0.14)', color: 'primary.main' }} />
        )}

        {trackSteps.length > 0 && (
          <Typography sx={{ mt: 1.25, fontSize: 14, fontWeight: 700, color: 'primary.main' }}>
            {doneTracks} / {trackSteps.length}
          </Typography>
        )}

        {collectionSteps.length > 0 && (
          <Box sx={{ mt: 1.5, width: '100%', maxWidth: 560, mx: 'auto', display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3, rowGap: 0.85, textAlign: 'left' }}>
            {collectionSteps.map((s) => stepRow(s))}
          </Box>
        )}

        {finalSteps.length > 0 && (
          <>
            <Box className="hb-divider" sx={{ mt: 2, mb: 1.5, width: '100%', maxWidth: 560, mx: 'auto' }} />
            <Box sx={{ width: '100%', maxWidth: 560, mx: 'auto', display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3, rowGap: 0.85, textAlign: 'left' }}>
              {finalSteps.map((s) => stepRow(s))}
            </Box>
          </>
        )}
      </Box>
    );
  };

  // ── Render: dossier library list ──
  const statusChip = (d) => {
    const inProgress = IN_PROGRESS.includes(d.status);
    const color = d.status === 'ready' ? '#3ddc97' : d.status === 'error' ? '#e06b6b' : '#19d0ef';
    return (
      <Chip size="small"
        icon={inProgress ? <CircularProgress size={11} thickness={5} sx={{ color: `${color} !important`, ml: '4px' }} /> : undefined}
        label={t(`heybro_status_${d.status}`, { defaultValue: d.status })}
        sx={{ height: 22, fontSize: 10.5, fontWeight: 700, color, bgcolor: `${color}1f` }} />
    );
  };

  const renderList = () => (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pt: 1.5, pr: 0.5 }}>
      {dossiersLoading && dossiers.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={26} sx={{ color: 'primary.main' }} />
        </Box>
      )}
      {!dossiersLoading && dossiers.length === 0 && (
        <Typography sx={{ color: 'text.secondary', fontSize: 14, textAlign: 'center', mt: 5, px: 4 }}>
          {t('heybro_list_empty')}
        </Typography>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25 }}>
        {dossiers.map((d) => (
          <Box key={d.id} className="hb-card-in" onClick={() => openDossier(d.id)}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', p: 1.75,
              bgcolor: 'rgba(255,255,255,0.03)', cursor: 'pointer', transition: 'border-color 0.2s, background-color 0.2s',
              '&:hover': { borderColor: 'rgba(25,208,239,0.5)', bgcolor: 'rgba(25,208,239,0.05)' } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.company_name}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
                  {[d.industry, d.region].filter(Boolean).join(' · ') || d.offering}
                </Typography>
              </Box>
              {statusChip(d)}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', flex: 1 }}>
                {formatDate(d.created_at, i18n.language)}
                {d.created_by_name ? ` · ${d.created_by_name}` : ''}
              </Typography>
              {d.status === 'ready' && (
                <InfoTooltip title={t('heybro_refresh')}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); refreshDossier(d.id); }}
                    sx={{ color: 'text.secondary', p: 0.5, '&:hover': { color: 'primary.main' } }}>
                    <RefreshIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InfoTooltip>
              )}
              <InfoTooltip title={t('heybro_delete')}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, dossier: d }); }}
                  sx={{ color: 'text.secondary', p: 0.5, '&:hover': { color: 'error.main' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InfoTooltip>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  // ── Render: chat tab ──
  const renderAssistant = () => (
    <>
      <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box ref={chatScrollRef} onScroll={handleScroll}
          sx={{ position: 'relative', flex: 1, overflowY: 'auto', mt: '14px', pr: 1.5 }}>
          {messages.length === 0 && (
            <Typography sx={{ color: 'text.secondary', fontSize: '14px', textAlign: 'center', mt: 4, px: 4, whiteSpace: 'pre-line' }}>
              {t('heybro_intro')}
            </Typography>
          )}
          {messages.map((m, i) => {
            const isUser = m.role === 'user';
            const isLoadingBubble = !isUser && !m.content;
            return (
              <Box key={i} className={isUser ? 'chat-user-msg' : undefined} sx={{ ...chatRowSx(isUser), mb: 1 }}>
                {!isUser && <BotAvatar />}
                <Box
                  sx={isLoadingBubble
                    ? { px: 0.5, bgcolor: 'transparent', color: 'text.primary', display: 'flex',
                        alignItems: 'center', minHeight: `${FIRST_LINE_SLOT_PX}px` }
                    : { ...chatBubbleSx(isUser), whiteSpace: isUser ? 'pre-wrap' : 'normal' }}
                >
                  {isUser
                    ? m.content
                    : (m.content
                      ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>{m.content}</ReactMarkdown>
                      : <CircularProgress size={22} thickness={4} sx={{ color: 'primary.main' }} />)}
                </Box>
              </Box>
            );
          })}
          <div ref={bottomSpacerRef} style={{ flexShrink: 0 }} />
          <div ref={messagesEndRef} />
        </Box>
        <ScrollToBottomButton show={showScrollDown} onClick={scrollToBottom} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={5}
          size="small"
          variant="outlined"
          placeholder={isTranscribing ? '' : t('heybro_placeholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={busy || isTranscribing}
          InputProps={{
            startAdornment: isTranscribing ? (
              <InputAdornment position="start">
                <CircularProgress size={18} sx={{ color: 'primary.main' }} />
              </InputAdornment>
            ) : null,
          }}
        />
        <InfoTooltip title={t('send_tooltip')}>
          <span style={{ display: 'inline-flex' }}>
            <IconButton color="primary" onClick={send}
              disabled={busy || isRecording || isTranscribing || !input.trim()} aria-label="send">
              {busy ? <CircularProgress size={22} /> : <SendRoundedIcon />}
            </IconButton>
          </span>
        </InfoTooltip>
        <InfoTooltip title={isRecording ? t('stt_stop_tooltip') : t('stt_tooltip')}>
          <span style={{ display: 'inline-flex' }}>
            <IconButton onClick={() => toggleRecording(STT_FIELD)} disabled={busy || isTranscribing}
              aria-label="Speech to text" sx={{ color: isRecording ? 'error.main' : 'text.secondary' }}>
              {isRecording ? <FiberManualRecordIcon fontSize="small" /> : <MicIcon fontSize="small" />}
            </IconButton>
          </span>
        </InfoTooltip>
      </Box>
    </>
  );

  // ── Render: dossiers tab (list | live | magazine) ──
  const renderDossiersTab = () => {
    if (view?.mode === 'live') {
      return (
        <Box className="hb-screen hb-screen-fwd" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => { stopPolling(); setView(null); fetchDossiers(); }}
              sx={{ textTransform: 'none', fontSize: 12.5, color: 'text.secondary' }}>
              {t('heybro_back')}
            </Button>
          </Box>
          {renderLiveView()}
        </Box>
      );
    }
    if (view?.mode === 'dossier') {
      return (
        <Box className="hb-screen hb-screen-fwd" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pt: 1 }}>
          <DossierView
            report={view.report}
            onBack={() => { setView(null); fetchDossiers(); }}
            onRefresh={() => refreshDossier(view.report.id)}
            onDelete={() => setDeleteDialog({ open: true, dossier: view.report })}
            onExport={() => exportDossier(view.report.id, view.report.company_name)}
            translating={translating}
          />
        </Box>
      );
    }
    return (
      <Box className="hb-screen hb-screen-back" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {renderList()}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <ModalHeader
        icon={<HealthAndSafetyIcon />}
        title={t('heybro_insurance')}
        onClose={onClose}
        sx={{ pb: 1 }}
        extra={tab === 0 && (
          <InfoTooltip title={messages.length === 0 ? t('no_chat_history') : ''}>
            <span>
              <Button
                onClick={clearChat}
                disabled={isRecording || isTranscribing || messages.length === 0}
                size="small"
                startIcon={<DeleteOutlineIcon />}
                sx={{ ...modalHeaderBtnSx, px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600 }}
              >
                {t('clear_chat')}
              </Button>
            </span>
          </InfoTooltip>
        )}
      />

      <Tabs
        value={tab}
        onChange={(e, v) => { setTab(v); if (v === 1) fetchDossiers(); }}
        variant="fullWidth"
        sx={{
          minHeight: 40,
          borderBottom: '1px solid rgba(25, 208, 239, 0.35)',
          '& .MuiTab-root': {
            minHeight: 40,
            textTransform: 'none',
            fontSize: 'var(--el-font-size-button)',
            color: 'text.secondary',
            '&.Mui-selected': { color: 'primary.main' },
          },
        }}
      >
        <Tab label={t('heybro_tab_assistant')} />
        <Tab label={t('heybro_tab_dossiers')} />
      </Tabs>

      {tab === 0 ? renderAssistant() : renderDossiersTab()}

      <AreDisclaimer textKey="heybro_disclaimer" sx={{ mt: 1 }} />

      {/* ─── Delete confirmation ─── */}
      <Dialog open={deleteDialog.open} onClose={() => !deleting && setDeleteDialog({ open: false, dossier: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>{t('heybro_delete_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('heybro_delete_desc', { company: deleteDialog.dossier?.company_name || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, dossier: null })} disabled={deleting}>
            {t('cancel', { defaultValue: 'Abbrechen' })}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : t('delete', { defaultValue: 'Löschen' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HeyBroInsurance;
